import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiLoader, FiPlay, FiPause, FiDownload, FiX, FiMusic, FiRefreshCw, FiActivity } from "react-icons/fi";
import { apiRequest, errorMessage } from "../api";
import { Studio, GRADES, CAPTION_STYLES, FIT_STYLES, DEFAULT_LOOK, loadImage, loadAudioBuffer, loadVideo, recordVideo } from "../video/studio";
import { MOTION_LABELS, TRANSITION_LABELS, SIZES, recordableFormats } from "../video/timeline";
import { analyseSpeech } from "../video/lipsync";
import { buildPresenter, isCutout } from "../video/avatar";
import { PRESENTERS, PRESENTER_IDS } from "../video/presenters";
import { DEFAULT_STAGE } from "../video/stage";
import StageControls from "./avatar/StageControls";
import PresenterThumb from "./avatar/PresenterThumb";

// Shared by the two video tools: the small building blocks, and the studio (preview, look, animation, saving the video).
export const LANGUAGES = { en: "English", ur: "Urdu", hi: "Hindi", pa: "Punjabi", ar: "Arabic" };
export const FALLBACK_STYLES = [["cinematic", "Cinematic"], ["realistic", "Realistic"], ["documentary", "Documentary"], ["aerial", "Aerial / drone"], ["anime", "Anime"], ["3d", "3D animation"], ["watercolor", "Watercolor"], ["vintage", "Vintage film"]];
export const RATIOS = [["16:9", "Landscape 16:9", "YouTube, websites"], ["9:16", "Tall 9:16", "Reels, TikTok, Shorts"], ["1:1", "Square 1:1", "Instagram, Facebook"]];
export const PREVIEW = { "16:9": [960, 540], "9:16": [540, 960], "1:1": [720, 720] };

export const inputClass = "w-full px-4 py-3 rounded-xl bg-navy border border-navy-border text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-accent-light";
export const labelClass = "text-xs uppercase tracking-widest text-white/50 font-semibold block mb-2.5";
export const pillOn = "bg-accent-light text-navy border-accent-light";
export const pillOff = "bg-white/5 border-navy-border text-gray-300 hover:border-accent-light";
export const ghost = "px-3.5 py-2 rounded-full border border-white/20 hover:border-accent-light text-xs font-medium inline-flex items-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed";
export const card = "rounded-2xl bg-white/5 border border-navy-border p-5 md:p-6";

export const slug = (text) => (String(text || "video").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40).replace(/^-|-$/g, "") || "video");

export function saveBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function Pills({ value, options, onChange, label }) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-2">
      {options.map(([key, text]) => (
        <button key={key} type="button" onClick={() => onChange(key)} aria-pressed={value === key}
          className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${value === key ? pillOn : pillOff}`}>{text}</button>
      ))}
    </div>
  );
}

export function Slider({ label, value, min, max, step = 1, onChange, suffix = "" }) {
  return (
    <label className="block">
      <span className="flex items-center justify-between text-xs text-gray-300 mb-1.5">{label}<span className="text-gray-500 tabular-nums">{value}{suffix}</span></span>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-[#22d3ee]" aria-label={label} />
    </label>
  );
}

export const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;


// ------------------------------------------------------------------------------------------------ the studio for a cinematic video
export const ANIMATE_HINT = "Each scene is animated by a free AI video model on a shared graphics card: about 1-4 minutes per scene (a speaking scene can take longer), and when the models are busy it may not work - the scene then keeps its camera movement.";

export const AVATAR_ANIMATE_HINT = "AI lip-sync turns your photo into a real moving video of the person speaking. It uses free AI models on shared graphics cards, which are very often busy or out of free capacity: when that happens nothing is lost - the lip-sync you see now stays. A part can take several minutes.";

export function StudioPanel({ view, segments, clientId }) {
  const canvasRef = useRef(null);
  const studioRef = useRef(null);
  const audioRef = useRef({ context: null, stop: null });
  const musicRef = useRef({ buffer: null, name: "" });
  const cancelRef = useRef({ cancelled: false });
  const aliveRef = useRef(true);
  const timeRef = useRef(0);
  const playingRef = useRef(false);
  const clock = useRef({ start: 0, from: 0 });
  const [scenes, setScenes] = useState(null);
  const [loadError, setLoadError] = useState("");
  const isAvatar = segments.source === "avatar";
  const avatarIsPhoto = isAvatar && segments.avatar?.type === "photo";
  const presenterRef = useRef(null);
  const [look, setLook] = useState({
    ...DEFAULT_LOOK, fit: segments.source === "images" ? "auto" : "fill", watermark: "",
    ...(isAvatar ? { grade: "none", vignette: 0.2, grain: 0, title: "" } : { title: segments.title || view.original_name || "" }),
  });
  const [stageCfg, setStageCfg] = useState(() => ({
    stage: { ...DEFAULT_STAGE, ...(segments.avatar?.stage || {}) }, name: segments.avatar?.name || "", role: segments.avatar?.role || "", presenter: segments.avatar?.presenter || PRESENTER_IDS[0],
  }));
  const [time, setTimeState] = useState(0);
  const [total, setTotal] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [resolution, setResolution] = useState("720p");
  const [formats] = useState(() => recordableFormats());
  const [format, setFormat] = useState(0);
  const [rendering, setRendering] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState("");
  const [musicName, setMusicName] = useState("");
  const [anim, setAnim] = useState({ busy: false, text: "", error: "", notice: "" });
  const ratio = segments.ratio || "16:9";
  const setTime = (t) => { timeRef.current = t; setTimeState(t); };

  // load the pictures, the narration and any clips of every scene
  useEffect(() => {
    let alive = true;
    aliveRef.current = true;
    const context = new (window.AudioContext || window.webkitAudioContext)();
    const audio = audioRef.current;
    audio.context = context;
    (async () => {
      try {
        // a presenter video: one presenter (a drawn studio presenter, or the photo) shared by every part, each part with the lip-sync of its own narration
        const parts = segments.scenes || [];
        const av = segments.avatar || {};
        const photoImg = isAvatar && av.type === "photo" && parts[0]?.image ? await loadImage(parts[0].image).catch(() => null) : null;
        const stageImg = isAvatar && av.background_image ? await loadImage(av.background_image).catch(() => null) : null;
        const presenter = isAvatar ? buildPresenter({ ...av, stage: { ...av.stage, ...stageCfg.stage } }, { img: photoImg, bg: stageImg, hasAlpha: isCutout(parts[0]?.image), seed: view.id || 1 }) : null;
        presenterRef.current = presenter;
        const loaded = await Promise.all(parts.map(async (s) => {
          const audioBuffer = s.audio ? await loadAudioBuffer(context, s.audio).catch(() => null) : null;
          return {
            ...s,
            img: isAvatar ? photoImg : (s.image ? await loadImage(s.image) : null),
            audioBuffer,
            analysis: isAvatar && audioBuffer ? analyseSpeech(audioBuffer) : null,
            presenter,
            video: s.clip ? await loadVideo(s.clip).catch(() => null) : null,
            clipTalking: !!s.clip_talking, clipEngine: s.clip_engine || "", useStill: false,
            actionText: s.action || "", speaks: !!s.speaks,
            motion: "auto", transition: isAvatar ? "cut" : "fade",
          };
        }));
        if (alive) setScenes(loaded);
      } catch (e) {
        if (alive) setLoadError("The pictures could not be loaded into the studio. Reload the page and open the video again.");
      }
    })();
    return () => { alive = false; aliveRef.current = false; cancelRef.current.cancelled = true; if (audio.stop) audio.stop(); context.close().catch(() => {}); };
  }, [segments]); // eslint-disable-line react-hooks/exhaustive-deps -- loads once per video: the stage and the view are read as they were when it opened

  // the preview studio
  useEffect(() => {
    if (!scenes || !canvasRef.current) return;
    if (!studioRef.current) {
      studioRef.current = new Studio(canvasRef.current, scenes, look, PREVIEW[ratio] || PREVIEW["16:9"]);
      studioRef.current.onVideoFrame = () => { if (!playingRef.current && studioRef.current) studioRef.current.drawFrame(timeRef.current, false); };
    } else studioRef.current.setScenes(scenes);
    studioRef.current.setLook(look);
    studioRef.current.drawFrame(timeRef.current, playingRef.current);
    setTotal(studioRef.current.total);
  }, [scenes, look, ratio]);


  // the presenter is shared by all the parts: changing the stage, the name or the person changes every part at once
  useEffect(() => {
    const p = presenterRef.current;
    if (!p) return;
    p.stage = stageCfg.stage;
    p.name = stageCfg.name;
    p.role = stageCfg.role;
    if (p.kind === "builtin") p.spec = PRESENTERS[stageCfg.presenter] || p.spec;
    if (studioRef.current) studioRef.current.drawFrame(timeRef.current, playingRef.current);
  }, [stageCfg, scenes]);
  const pickStagePicture = async (file) => {
    if (!file || !presenterRef.current || !file.type.startsWith("image/")) return;
    const url = URL.createObjectURL(file);
    try {
      presenterRef.current.bg = await loadImage(url);
      setStageCfg((c) => ({ ...c, stage: { ...c.stage, background: "image" } }));
      setHasStagePicture(true);
    } catch { setMessage("That picture could not be opened."); }
  };
  const [hasStagePicture, setHasStagePicture] = useState(!!segments.avatar?.background_image);
  const clearStagePicture = () => {
    if (presenterRef.current) presenterRef.current.bg = null;
    setHasStagePicture(false);
    setStageCfg((c) => ({ ...c, stage: { ...c.stage, background: c.stage.background === "image" ? "studio" : c.stage.background } }));
  };

  const stopAudio = () => { if (audioRef.current.stop) { audioRef.current.stop(); audioRef.current.stop = null; } };
  const play = useCallback(async (from) => {
    const studio = studioRef.current;
    if (!studio) return;
    const { context } = audioRef.current;
    await context.resume();
    stopAudio();
    audioRef.current.stop = studio.playAudio(context, context.destination, from, musicRef.current);
    clock.current = { start: performance.now(), from };
    playingRef.current = true;
    setPlaying(true);
  }, []);
  const pause = useCallback(() => {
    stopAudio();
    playingRef.current = false;
    if (studioRef.current) { studioRef.current.pauseVideos(); studioRef.current.drawFrame(timeRef.current, false); }
    setPlaying(false);
  }, []);

  useEffect(() => {
    if (!playing) return undefined;
    const id = setInterval(() => {
      const studio = studioRef.current;
      const t = clock.current.from + (performance.now() - clock.current.start) / 1000;
      if (t >= studio.total) { setTime(studio.total); stopAudio(); playingRef.current = false; studio.pauseVideos(); studio.drawFrame(studio.total, false); setPlaying(false); return; }
      studio.drawFrame(t, true);
      setTime(t);
    }, 33);
    return () => clearInterval(id);
  }, [playing]);

  const seek = (t) => {
    setTime(t);
    studioRef.current?.drawFrame(t, false);
    if (playing) play(t);
  };
  const editScene = (i, patch) => setScenes((list) => list.map((s, k) => (k === i ? { ...s, ...patch } : s)));
  const setL = (patch) => setLook((l) => ({ ...l, ...patch }));

  const pickMusic = async (file) => {
    if (!file) return;
    try {
      const buffer = await audioRef.current.context.decodeAudioData(await file.arrayBuffer());
      musicRef.current = { buffer, name: file.name };
      setMusicName(file.name);
      setMessage("");
    } catch { setMessage("That music file could not be read. Use an MP3, WAV or M4A file."); }
  };

  // ---------------------------------------------------------------------------------------- bringing the characters to life
  const animate = async (indexes) => {
    if (anim.busy || !indexes.length) return;
    pause();
    setAnim({ busy: true, text: "Sending...", error: "", notice: "" });
    const form = new FormData();
    form.append("client_id", clientId);
    form.append("kind", "videoanim");
    form.append("parent", String(view.id));
    form.append("items", JSON.stringify(indexes.map((i) => ({ index: i, action: scenes[i].actionText, speaks: !!scenes[i].speaks && !!scenes[i].audioBuffer }))));
    const { ok, data } = await apiRequest("/core/voice-clone/", { method: "POST", body: form });
    if (!ok) { setAnim({ busy: false, text: "", error: data?.items || errorMessage(data), notice: "" }); return; }
    // wait for the job, then take its clips into the studio
    const url = `/core/voice-clone/${data.id}/?client_id=${encodeURIComponent(clientId)}`;
    let job = data;
    for (let n = 0; n < 900 && aliveRef.current && (job.status === "queued" || job.status === "processing"); n += 1) {
      setAnim({ busy: true, text: job.progress || "Working...", error: "", notice: "" });
      await new Promise((r) => setTimeout(r, 4000));
      const polled = await apiRequest(url);
      if (polled.ok) job = polled.data;
    }
    if (!aliveRef.current) return;
    if (job.status !== "completed") { setAnim({ busy: false, text: "", error: job.error_message || "The characters could not be animated. Please try again later.", notice: "" }); return; }
    const parent = await apiRequest(`/core/voice-clone/${view.id}/?client_id=${encodeURIComponent(clientId)}`);
    const fresh = parent.ok && parent.data.segments && !Array.isArray(parent.data.segments) ? parent.data.segments.scenes || [] : [];
    const result = job.segments && !Array.isArray(job.segments) ? job.segments : {};
    const loaded = {};
    await Promise.all((result.done || []).map(async (i) => {
      const f = fresh[i];
      if (f && f.clip) loaded[i] = { video: await loadVideo(f.clip).catch(() => null), clipTalking: !!f.clip_talking, clipEngine: f.clip_engine || "", useStill: false };
    }));
    setScenes((list) => list.map((s, i) => (loaded[i] && loaded[i].video ? { ...s, ...loaded[i] } : s)));
    const failed = Object.keys(result.failed || {});
    setAnim({
      busy: false, text: "", error: failed.length ? `${failed.length === indexes.length ? "No scene" : `Scene ${failed.map((i) => Number(i) + 1).join(", ")}`} could not be animated: ${Object.values(result.failed)[0]}` : "",
      notice: result.notice || "",
    });
  };

  const render = async () => {
    if (!formats.length) { setMessage("This browser cannot save videos. Use a current Chrome, Edge, Firefox or Safari."); return; }
    setMessage("");
    setRendering(true);
    setProgress(0);
    pause();
    cancelRef.current = { cancelled: false };
    try {
      const size = SIZES[ratio][resolution];
      const canvas = document.createElement("canvas");
      const studio = new Studio(canvas, scenes, look, size);
      const blob = await recordVideo(studio, { mime: formats[format].mime, music: musicRef.current.buffer ? musicRef.current : null, bitrate: resolution === "1080p" ? 9e6 : 5e6, onProgress: setProgress, signal: cancelRef.current });
      saveBlob(`${slug(look.title || view.original_name)}.${formats[format].ext}`, blob);
      setMessage(`Saved ${(blob.size / 1048576).toFixed(1)} MB.`);
    } catch (e) {
      setMessage(e.message === "cancelled" ? "Stopped." : `The video could not be saved (${e.message || "unknown error"}).`);
    }
    studioRef.current?.drawFrame(timeRef.current, false);
    setRendering(false);
  };

  if (loadError) return <div className={`${card} mt-6 text-sm text-red-200`}>{loadError}</div>;
  if (!scenes) return <div className={`${card} mt-6 flex items-center gap-3 text-sm text-gray-300`}><FiLoader className="animate-spin" /> Opening the studio...</div>;

  const voiceLess = !scenes.some((s) => s.audioBuffer);
  const animated = scenes.filter((s) => s.video).length;
  const withPicture = scenes.map((s, i) => (s.img ? i : -1)).filter((i) => i >= 0);
  return (
    <div className={`${card} mt-6 border-accent-light/30`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0"><p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-1">Video studio</p><p className="font-semibold" dir="auto">{view.original_name}</p></div>
        {view.engine && <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-gray-200">{isAvatar ? "Presenter" : "Pictures"}: {view.engine}</span>}
      </div>
      {segments.notice && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-4">{segments.notice}</p>}

      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-6 items-start">
        <div className="lg:sticky lg:top-24">
          <div className="rounded-xl overflow-hidden bg-black flex justify-center">
            <canvas ref={canvasRef} className={`block ${ratio === "9:16" ? "max-h-[600px] w-auto" : "w-full"}`} style={{ aspectRatio: ratio.replace(":", " / ") }} aria-label="Video preview" />
          </div>
          <div className="flex items-center gap-3 mt-3">
            <button type="button" onClick={() => (playing ? pause() : play(time >= total - 0.05 ? 0 : time))} className="w-11 h-11 rounded-full bg-brand-gradient text-white flex items-center justify-center shrink-0" aria-label={playing ? "Pause" : "Play"}>{playing ? <FiPause /> : <FiPlay />}</button>
            <input type="range" min="0" max={Math.max(0.1, total)} step="0.05" value={Math.min(time, total)} onChange={(e) => seek(Number(e.target.value))} className="flex-1 accent-[#22d3ee]" aria-label="Position in the video" />
            <span className="text-xs text-gray-400 tabular-nums w-24 text-end">{fmt(time)} / {fmt(total)}</span>
          </div>
          {voiceLess && <p className="text-xs text-gray-500 mt-2">This video has no narration. You can add music below.</p>}
        </div>

        <div className="space-y-5">
          {isAvatar && (
            <div className="space-y-5 pb-5 border-b border-navy-border">
              {!avatarIsPhoto && (
                <div>
                  <span className={labelClass}>Presenter</span>
                  <div className="grid grid-cols-4 gap-2">
                    {PRESENTER_IDS.map((id) => (
                      <button key={id} type="button" aria-pressed={stageCfg.presenter === id} aria-label={PRESENTERS[id].name} onClick={() => setStageCfg((c) => ({ ...c, presenter: id }))}
                        className={`rounded-lg overflow-hidden border-2 ${stageCfg.presenter === id ? "border-accent-light" : "border-transparent hover:border-white/30"}`}><PresenterThumb id={id} size={90} className="w-full h-auto" /></button>
                    ))}
                  </div>
                </div>
              )}
              <StageControls stage={stageCfg.stage} onStage={(patch) => setStageCfg((c) => ({ ...c, stage: { ...c.stage, ...patch } }))} name={stageCfg.name} role={stageCfg.role}
                onName={(name) => setStageCfg((c) => ({ ...c, name }))} onRole={(role) => setStageCfg((c) => ({ ...c, role }))}
                background={hasStagePicture} onPickBackground={pickStagePicture} onClearBackground={clearStagePicture} idPrefix="studio" compact stageUsed={!avatarIsPhoto || isCutout(segments.scenes?.[0]?.image)} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><label htmlFor="v-title" className={labelClass}>Title card</label><input id="v-title" className={inputClass} value={look.title} maxLength={80} onChange={(e) => setL({ title: e.target.value })} dir="auto" placeholder="(none)" /></div>
            <div><label htmlFor="v-titlesub" className={labelClass}>Under the title</label><input id="v-titlesub" className={inputClass} value={look.titleSub} maxLength={80} onChange={(e) => setL({ titleSub: e.target.value })} dir="auto" /></div>
            <div><label htmlFor="v-outro" className={labelClass}>Closing card</label><input id="v-outro" className={inputClass} value={look.outro} maxLength={80} onChange={(e) => setL({ outro: e.target.value })} dir="auto" placeholder="e.g. Thank you for watching" /></div>
            <div><label htmlFor="v-outrosub" className={labelClass}>Under it</label><input id="v-outrosub" className={inputClass} value={look.outroSub} maxLength={80} onChange={(e) => setL({ outroSub: e.target.value })} dir="auto" placeholder="e.g. www.yoursite.com" /></div>
          </div>
          <div><span className={labelClass}>Captions</span><Pills label="Captions" value={look.captions} onChange={(captions) => setL({ captions })} options={Object.entries(CAPTION_STYLES)} /></div>
          {!isAvatar && <div><span className={labelClass}>Pictures</span><Pills label="Pictures" value={look.fit} onChange={(fit) => setL({ fit })} options={Object.entries(FIT_STYLES)} /></div>}
          {!isAvatar && <div><span className={labelClass}>Colour look</span><Pills label="Colour look" value={look.grade} onChange={(grade) => setL({ grade })} options={Object.entries(GRADES).map(([k, g]) => [k, g.label])} /></div>}
          <div className={`grid grid-cols-2 gap-3 ${isAvatar ? "hidden" : ""}`}>
            <div>
              <label htmlFor="v-motion" className={labelClass}>Camera</label>
              <select id="v-motion" className={inputClass} value={look.motion} onChange={(e) => { setL({ motion: e.target.value }); setScenes((l) => l.map((s) => ({ ...s, motion: "auto" }))); }}>
                {Object.entries(MOTION_LABELS).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="v-trans" className={labelClass}>Transitions</label>
              <select id="v-trans" className={inputClass} value={look.transition} onChange={(e) => { setL({ transition: e.target.value }); setScenes((l) => l.map((s) => ({ ...s, transition: e.target.value }))); }}>
                {Object.entries(TRANSITION_LABELS).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Slider label="Vignette" value={Math.round(look.vignette * 100)} min={0} max={80} step={5} suffix="%" onChange={(v) => setL({ vignette: v / 100 })} />
            <Slider label="Film grain" value={Math.round(look.grain * 100)} min={0} max={25} step={1} suffix="%" onChange={(v) => setL({ grain: v / 100 })} />
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer"><input type="checkbox" checked={look.letterbox} onChange={(e) => setL({ letterbox: e.target.checked })} className="accent-[#22d3ee] w-4 h-4" /> Cinema bars</label>
            <div className="flex items-center gap-2"><input type="color" value={look.brandA} onChange={(e) => setL({ brandA: e.target.value })} aria-label="Card colour one" className="w-8 h-8 rounded border border-navy-border bg-transparent p-0.5" /><input type="color" value={look.brandB} onChange={(e) => setL({ brandB: e.target.value })} aria-label="Card colour two" className="w-8 h-8 rounded border border-navy-border bg-transparent p-0.5" /><span className="text-xs text-gray-400">card colours</span></div>
          </div>
          <div><label htmlFor="v-mark" className={labelClass}>Watermark text</label><input id="v-mark" className={inputClass} value={look.watermark} maxLength={40} onChange={(e) => setL({ watermark: e.target.value })} placeholder="e.g. @yourbrand" dir="auto" /></div>
          <div>
            <span className={labelClass}><FiMusic className="inline -mt-0.5" size={11} /> Background music <span className="normal-case tracking-normal text-gray-500 font-normal">(your own file)</span></span>
            <div className="flex flex-wrap items-center gap-3">
              <label className={`${ghost} cursor-pointer`}>Choose a music file<input type="file" accept="audio/*" className="hidden" onChange={(e) => { pickMusic(e.target.files?.[0]); e.target.value = ""; }} /></label>
              {musicName && <><span className="text-xs text-gray-300 truncate max-w-[10rem]">{musicName}</span><button type="button" onClick={() => { musicRef.current = { buffer: null, name: "" }; setMusicName(""); }} className="text-gray-400 hover:text-red-400" aria-label="Remove music"><FiX size={14} /></button></>}
            </div>
            {musicName && <div className="mt-3"><Slider label="Music volume" value={Math.round(look.musicVolume * 100)} min={2} max={60} step={2} suffix="%" onChange={(v) => setL({ musicVolume: v / 100 })} /></div>}
            <p className="text-xs text-gray-500 mt-2">Use music you have the right to use. The page does not add any.</p>
          </div>
        </div>
      </div>

      {(!isAvatar || avatarIsPhoto) && <div className="mt-6 border-t border-navy-border pt-5">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div className="min-w-0 flex-1">
            <p className={labelClass}><FiActivity className="inline -mt-0.5" size={11} /> {isAvatar ? "AI lip-sync of your photo (experimental)" : "Bring the characters to life"}</p>
            <p className="text-xs text-gray-400 max-w-2xl">{isAvatar ? AVATAR_ANIMATE_HINT : `Turn a scene's picture into a moving clip: people's hands, arms, legs, head and lips move. Describe what should move; tick "says this line" and the lips follow the narration. ${ANIMATE_HINT}`}</p>
          </div>
          <button type="button" onClick={() => animate(withPicture)} disabled={anim.busy || !withPicture.length} className="px-5 py-3 rounded-full bg-brand-gradient text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            {anim.busy ? <><FiLoader className="animate-spin" /> {anim.text}</> : <><FiActivity /> Animate all scenes ({withPicture.length})</>}
          </button>
        </div>
        {anim.error && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-3">{anim.error}</p>}
        {anim.notice && <p className="text-xs text-gray-300 bg-white/5 border border-navy-border rounded-xl px-4 py-3 mb-3">{anim.notice}</p>}
        {animated > 0 && <p className="text-xs text-emerald-300 mb-3">{animated} of {scenes.length} scenes are animated. Press play to see them move.</p>}
        {isAvatar && (
          <ol className="grid sm:grid-cols-2 gap-2">
            {scenes.map((s, i) => (
              <li key={i} className="flex items-center gap-3 rounded-xl border border-navy-border bg-navy/60 p-3">
                <span className="w-6 h-6 rounded-full bg-white/10 text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <p className="text-xs text-gray-300 line-clamp-2 flex-1" dir="auto">{s.text}</p>
                {s.video ? <span className="text-[10px] text-emerald-300 shrink-0">AI clip · {s.clipEngine}</span> : <button type="button" onClick={() => animate([i])} disabled={anim.busy || !s.img || !s.audioBuffer} className={`${ghost} !px-2.5 !py-1 !text-[11px] shrink-0`}><FiActivity size={11} /> Try AI</button>}
              </li>
            ))}
          </ol>
        )}
        <ol className={`grid sm:grid-cols-2 gap-3 ${isAvatar ? "hidden" : ""}`}>
          {scenes.map((s, i) => (
            <li key={i} className="flex gap-3 rounded-xl border border-navy-border bg-navy/60 p-3">
              <div className="relative shrink-0">
                {s.image ? <img src={s.image} alt={`Scene ${i + 1}`} className="w-28 h-20 object-cover rounded-lg" /> : <div className="w-28 h-20 rounded-lg bg-white/5 text-[10px] text-gray-500 flex items-center justify-center text-center px-1">no picture</div>}
                {s.video && !s.useStill && <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded-full bg-emerald-500/90 text-[9px] font-bold text-white">MOVING</span>}
              </div>
              <div className="min-w-0 flex-1 space-y-2">
                <p className="text-xs text-gray-300 line-clamp-2" dir="auto">{s.text || s.visual}</p>
                <input aria-label={`Scene ${i + 1}: what moves`} className="w-full bg-navy border border-navy-border rounded-md text-[11px] px-2 py-1.5 placeholder:text-gray-600" value={s.actionText} maxLength={220} dir="auto" placeholder="What moves: hands, legs, lips..." onChange={(e) => editScene(i, { actionText: e.target.value })} />
                <label className={`flex items-center gap-1.5 text-[11px] ${s.audioBuffer ? "text-gray-300 cursor-pointer" : "text-gray-600"}`}><input type="checkbox" disabled={!s.audioBuffer} checked={!!s.speaks && !!s.audioBuffer} onChange={(e) => editScene(i, { speaks: e.target.checked })} className="accent-[#22d3ee]" /> Says this line (lip-sync)</label>
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => animate([i])} disabled={anim.busy || !s.img} className={`${ghost} !px-2.5 !py-1 !text-[11px]`}><FiActivity size={11} /> {s.video ? "Animate again" : "Animate"}</button>
                  {s.video && <label className="flex items-center gap-1 text-[11px] text-gray-300 cursor-pointer"><input type="checkbox" checked={!s.useStill} onChange={(e) => editScene(i, { useStill: !e.target.checked })} className="accent-[#22d3ee]" /> use movement</label>}
                  {s.video && <span className="text-[10px] text-gray-500">{s.clipEngine}{s.clipTalking ? " · lips synced" : ""}</span>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <select aria-label={`Scene ${i + 1} camera`} className="bg-navy border border-navy-border rounded-md text-[11px] px-1.5 py-1" value={s.motion} onChange={(e) => editScene(i, { motion: e.target.value })}>{Object.entries(MOTION_LABELS).map(([k, t]) => <option key={k} value={k}>{t}</option>)}</select>
                  <select aria-label={`Scene ${i + 1} transition`} className="bg-navy border border-navy-border rounded-md text-[11px] px-1.5 py-1" value={s.transition} onChange={(e) => editScene(i, { transition: e.target.value })}>{Object.entries(TRANSITION_LABELS).map(([k, t]) => <option key={k} value={k}>{t}</option>)}</select>
                  <label className="flex items-center gap-1 text-[11px] text-gray-400">Length <input type="number" aria-label={`Scene ${i + 1} length in seconds`} min={s.audioBuffer ? Math.ceil((s.audioBuffer.duration + 0.7) * 10) / 10 : 2} max={14} step={0.5} value={Number(s.seconds).toFixed(1)}
                    onChange={(e) => editScene(i, { seconds: Math.max(s.audioBuffer ? s.audioBuffer.duration + 0.7 : 2, Math.min(14, Number(e.target.value) || 2)) })} className="w-14 bg-navy border border-navy-border rounded-md text-[11px] px-1 py-1" /> s</label>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>}

      <div className="mt-6 border-t border-navy-border pt-5 flex flex-wrap items-end gap-4">
        <div><span className={labelClass}>Quality</span><Pills label="Quality" value={resolution} onChange={setResolution} options={[["720p", "720p HD"], ["1080p", "1080p Full HD"]]} /></div>
        {formats.length > 1 && <div><span className={labelClass}>File type</span><Pills label="File type" value={String(format)} onChange={(v) => setFormat(Number(v))} options={formats.map((f, i) => [String(i), f.label])} /></div>}
        <button type="button" onClick={rendering ? () => { cancelRef.current.cancelled = true; } : render} disabled={!formats.length || anim.busy}
          className="ms-auto flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand-gradient text-white font-semibold disabled:opacity-50">
          {rendering ? <><FiLoader className="animate-spin" /> Recording {Math.round(progress * 100)}% - stop</> : <><FiDownload /> Save the video ({fmt(total)})</>}
        </button>
      </div>
      <p className="text-xs text-gray-500 mt-3">The video is put together in your browser, in real time (a {fmt(total)} video takes about {fmt(total + 1)}) - keep this tab open while it records. {formats.length ? "" : "This browser cannot record videos."}</p>
      {message && <p className="text-sm text-amber-200 mt-3 inline-flex items-center gap-2"><FiRefreshCw size={13} /> {message}</p>}
    </div>
  );
}
